import { Role } from "@model/role";
import type { OrgUserRole, RenameOrgUserResponse } from "@utils/api-communication/RestApiCli";

export const CREATABLE_ORG_USER_ROLES: readonly OrgUserRole[] = [
  Role.User,
  Role.Operator,
  Role.Manager,
  Role.Distributor,
  Role.Admin,
  Role.SuperAdmin,
  Role.Installer,
];
export type CreatableOrgUserRole = OrgUserRole;

export type UsernameError = "required" | "whitespace" | "unchanged";
export type TemporaryPasswordError = "tooShort" | "uppercase" | "lowercase" | "digit";

// superadmin subsumes admin, mirroring role.utils.ts on the backend.
export function canManageOrgUsers(role: string | null | undefined): boolean {
  return role === Role.Admin || role === Role.SuperAdmin;
}

export function usernameError(username: string): UsernameError | null {
  const trimmed = username.trim();
  if (!trimmed) return "required";
  if (/\s/.test(trimmed)) return "whitespace";
  return null;
}

export function renameError(username: string, currentUsername: string): UsernameError | null {
  const error = usernameError(username);
  if (error) return error;
  return username.trim() === currentUsername ? "unchanged" : null;
}

// Mirrors validatePassword on the backend so the form rejects what the server would.
export function temporaryPasswordError(password: string): TemporaryPasswordError | null {
  if (password.length < 8) return "tooShort";
  if (!/[A-Z]/.test(password)) return "uppercase";
  if (!/[a-z]/.test(password)) return "lowercase";
  if (!/[0-9]/.test(password)) return "digit";
  return null;
}

export function totalMigratedRecords(
  migration: RenameOrgUserResponse["migration"] | undefined,
): number {
  if (!migration?.updated) return 0;
  return Object.values(migration.updated).reduce((sum, count) => sum + (count || 0), 0);
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string") return data;
  const message = (data as { message?: unknown })?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.filter((m) => typeof m === "string").join(" ");
  return fallback;
}
